import React, { useContext, useState } from 'react'

import { EnvironmentContext } from 'config/environment'
import {
  BillingAddonReferenceFragment,
  BypassSelfUpsellDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import useModuleDiscount from 'modules/upsell/hooks/useModuleDiscount'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import getF25Plans, { F25PlanDTO } from 'modules/upsell/lib/selfUpsell/plans/getF25Plans'
import {
  DEFAULT_CURRENCY,
  MONTH_PERIOD,
  SIX_MONTH_PERIOD,
  YEAR_PERIOD,
  getCountryFromPlan,
  getCurrentPeriod,
  mapToF25Addon,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { UpsellDiscount, VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { PeriodUnit } from 'modules/upsell/lib/selfUpsell/types'

import { SelfUpsellConfig } from './types'

type SelfUpsellContextType = {
  allAddons: F25PlanDTO
  allCrossSellingAddons: F25PlanDTO
  togglePlanSelect: (plan: Plan) => void
  toggleCrossSellingPlanSelect: (plan: Plan) => void
  setVariantForPlan: (variant: VariantsEnum, plan: Plan) => void
  currency: string
  currentSeats: number
  setCurrentSeats: (seats: number) => void
  seats: number
  setSeats: (seats: number) => void
  period: PeriodUnit
  configPlan: SelfUpsellConfig
  setConfigPlan: (configPlan: SelfUpsellConfig) => void
  isYearly: () => boolean
  computePerSeatPrice: () => number
  computeTotalPrice: () => number
  computeCrossSellingTotalPrice: () => number
  isExpenses: () => boolean
  isRecruitment: () => boolean
  shouldBypassSelfUpsell: boolean
  discount: UpsellDiscount | undefined
}

type SelfUpsellProviderProps = {
  children: React.ReactNode | React.ReactNode[]
  availableAddons: BillingAddonReferenceFragment[]
  availableCrossSellingAddons: BillingAddonReferenceFragment[]
  moduleName: string
}

const SelfUpsellContext = React.createContext<SelfUpsellContextType>({
  allAddons: {
    f25Addons: [],
  },
  allCrossSellingAddons: {
    f25Addons: [],
  },
  togglePlanSelect: () => {},
  toggleCrossSellingPlanSelect: () => {},
  setVariantForPlan: () => {},
  period: 'six_months',
  currency: DEFAULT_CURRENCY,
  seats: 0,
  setSeats: () => {},
  configPlan: {
    addon: undefined,
    variant: undefined,
  },
  currentSeats: 0,
  setCurrentSeats: () => {},
  setConfigPlan: () => {},
  isYearly: () => false,
  computePerSeatPrice: () => 0,
  computeTotalPrice: () => 0,
  computeCrossSellingTotalPrice: () => 0,
  isExpenses: () => false,
  isRecruitment: () => false,
  shouldBypassSelfUpsell: false,
  discount: {
    module: '',
    percentage: 0,
    period: '3',
    periodUnit: 'month',
  },
})

export const SelfUpsellProvider = ({
  children,
  availableAddons,
  availableCrossSellingAddons,
  moduleName,
}: SelfUpsellProviderProps) => {
  const { company } = useCurrentSession()
  const { data } = useQuery(BypassSelfUpsellDocument, {
    variables: { companyId: company.id },
  })
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const bypassSelfUpsell = data?.features.featuresConnection.nodes[0]?.enabled ?? false
  const shouldBypassSelfUpsell = bypassSelfUpsell || ignoreUpsell
  const legalEntityCountry = company.legalEntities.find((entity) => entity.main)?.country || ''
  const country = getCountryFromPlan(company.subscription.plan, legalEntityCountry)
  const currency = company.currency || DEFAULT_CURRENCY
  const period = shouldBypassSelfUpsell
    ? MONTH_PERIOD
    : getCurrentPeriod(company.subscription.plan) || SIX_MONTH_PERIOD
  const [seats, setSeats] = React.useState<number>(0)
  const [currentSeats, setCurrentSeats] = React.useState<number>(
    company.subscription.currentCycleQuantity || company.subscription.seatCount
  )
  const [allAddons, setAllAddons] = useState<F25PlanDTO>({
    f25Addons: [],
  })

  const [allCrossSellingAddons, setAllCrossSellingAddons] = useState<F25PlanDTO>({
    f25Addons: [],
  })

  const [configPlan, setConfigPlan] = React.useState<SelfUpsellConfig>({
    addon: undefined,
    variant: undefined,
  })

  const { discount: discountData } = useModuleDiscount({
    moduleName,
  })

  const discount: UpsellDiscount | undefined = discountData
    ? {
        module: discountData.module,
        percentage: discountData.percentage,
        period: discountData.period.toString(),
        periodUnit: discountData.periodUnit,
      }
    : undefined

  const isYearly = () => {
    return period === YEAR_PERIOD
  }

  const isExpenses = () => {
    return allAddons.f25Addons.some((addon) =>
      addon.internalTitle.toLowerCase().includes('expenses')
    )
  }

  const isRecruitment = () => {
    return allAddons.f25Addons.some((addon) =>
      addon.internalTitle.toLowerCase().includes('recruitment')
    )
  }

  const togglePlanSelect = (plan: Plan) => {
    allAddons.f25Addons.forEach((auxPlan) => {
      auxPlan.unselect()
    })

    plan.toggleSelection()
    setAllAddons({ ...allAddons })
  }

  const toggleCrossSellingPlanSelect = (plan: Plan) => {
    plan.toggleSelection()
    setAllCrossSellingAddons({ ...allCrossSellingAddons })
  }

  const setVariantForPlan = (variant: VariantsEnum, plan: Plan) => {
    allAddons.f25Addons.concat(allCrossSellingAddons.f25Addons).forEach((auxPlan) => {
      if (plan.internalTitle === auxPlan.internalTitle) {
        auxPlan.setActiveVariant(variant)
        return
      }
    })
    setAllAddons({ ...allAddons })
  }

  const computePrice = (priceHandler: (plan: Plan) => number | undefined) => {
    return allAddons.f25Addons.concat(allCrossSellingAddons.f25Addons).reduce((acc, cur) => {
      if (cur?.isSelected) {
        const price = priceHandler(cur) || 0
        return acc + price
      }
      return acc
    }, 0)
  }

  const computePerSeatPrice = () => {
    return computePrice((cur) => cur.pricing.getSeatsPrice(period))
  }

  const computeTotalPrice = () => {
    const customSeats = isExpenses() ? seats : currentSeats
    return computePrice((cur) => {
      if (allAddons.f25Addons.includes(cur)) {
        return cur.pricing.getTotalPrice(period, customSeats)
      }

      return 0
    })
  }

  const computeCrossSellingTotalPrice = () => {
    return computePrice((cur) => {
      if (allCrossSellingAddons.f25Addons.includes(cur)) {
        return cur.pricing.getTotalPrice(period, currentSeats)
      }
      return 0
    })
  }

  useUnsafeEffect(() => {
    if (availableAddons.length) {
      const plans = getF25Plans({ allF25Addons: mapToF25Addon(availableAddons) }, country)
      setAllAddons({ f25Addons: plans.f25Addons })
    }

    if (availableCrossSellingAddons.length) {
      const plans = getF25Plans(
        { allF25Addons: mapToF25Addon(availableCrossSellingAddons) },
        country
      )

      setAllCrossSellingAddons({ f25Addons: plans.f25Addons })
    }
  }, [availableAddons, availableCrossSellingAddons, country])

  return (
    <SelfUpsellContext.Provider
      value={{
        allAddons,
        allCrossSellingAddons,
        togglePlanSelect,
        toggleCrossSellingPlanSelect,
        setVariantForPlan,
        period,
        currency,
        currentSeats,
        setCurrentSeats,
        seats,
        setSeats,
        configPlan,
        setConfigPlan,
        isYearly,
        computePerSeatPrice,
        computeTotalPrice,
        computeCrossSellingTotalPrice,
        isExpenses,
        isRecruitment,
        shouldBypassSelfUpsell,
        discount,
      }}
    >
      {children}
    </SelfUpsellContext.Provider>
  )
}

export const useSelfUpsellContext = () => useContext(SelfUpsellContext)
